import { useEffect } from "react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { useT } from "../i18n";
import { schreibtNoch } from "./ruhe";
import { istLadefehler } from "./ladeart";

/* ══════════════════════════════════════════════════════════════════
   Wenn eine Maske nicht zeichnen kann — die Seite statt „Something went
   wrong!" (02.10.2026).

   Lisa am Telefon, mitten in der Übergabe, während eines Anrufs ohne
   Datenverbindung: *„Importing binding name 'c' is not found."* Das ist kein
   Fehler im Bau — nachgezählt an beiden Fassungen, jede Bindung hat ihr Ziel —,
   sondern Safari: Bricht das Laden eines Stücks ab, behält es den halben
   Stand, und der nächste Versuch scheitert beim Verknüpfen. **Ein Neuladen
   heilt es**, und der Protokollentwurf liegt auf dem Gerät
   (`daten/entwurf.ts`); was im Funkloch gespeichert wurde, in der
   Warteschlange.

   Deshalb:

   - **Ein Ladefehler lädt die Seite von selbst neu** — höchstens einmal je
     Minute, damit ein Stück, das wirklich fehlt, keine Schleife wird. Danach
     steht diese Seite mit dem Knopf da.
   - **Nicht, solange etwas unterwegs ist** (`schreibtNoch`): Ein Protokoll auf
     dem Weg zum Server darf kein Neuladen abschneiden.
   - **Jeder andere Fehler** bekommt dieselbe Seite, auf Deutsch, mit dem
     Wortlaut darunter — damit ein Bildschirmfoto reicht, um ihn zu finden.
   ══════════════════════════════════════════════════════════════════ */

const SCHLUESSEL = "mo-ladefehler-neu-geladen";
const SPERRE_MS = 60_000;

/** Neu laden, wenn es in der letzten Minute nicht schon geschehen ist. `true`, wenn es geschieht. */
export function einmalNeuLaden(): boolean {
  let zuletzt = 0;
  try { zuletzt = Number(sessionStorage.getItem(SCHLUESSEL) ?? 0); } catch { /* privates Fenster */ }
  if (Date.now() - zuletzt < SPERRE_MS || schreibtNoch()) return false;
  try { sessionStorage.setItem(SCHLUESSEL, String(Date.now())); } catch { /* ohne Speicher trotzdem */ }
  window.location.reload();
  return true;
}

export function Ladefehler({ error }: ErrorComponentProps) {
  const t = useT();
  const ladefehler = istLadefehler(error);

  useEffect(() => {
    if (ladefehler) einmalNeuLaden();
  }, [ladefehler]);

  const wortlaut = error instanceof Error ? error.message : String(error);
  return (
    <main className="ladefehler" role="alert">
      <h1>{t(ladefehler ? "ladefehler.titelLaden" : "ladefehler.titel")}</h1>
      <p>{t(ladefehler ? "ladefehler.satzLaden" : "ladefehler.satz")}</p>
      <button type="button" className="knopf haupt" onClick={() => window.location.reload()}>
        {t("ladefehler.neuLaden")}
      </button>
      <p className="ladefehler-wortlaut">{wortlaut}</p>
    </main>
  );
}
